import {
  CURRENT_DAY,
  DEFAULT_INITIAL_BILLING_OPTIONS,
  INVALID_NON_WORKING_DAYS_LENGTH,
  MONTHS,
  NON_WORKING_WEEKEND_DAYS
} from '../constants/print-store'
import { GetPriceForQuantityResponse, PrintStoreProductsType } from '../types/print-store.types'
import {
  PriceTier,
  PriceType,
  Product,
  ShippingOption,
  ShoppingCartItem
} from '@dtx-company/flow-codegen/src/code/generated.types'
import { roundPrice } from '../components/print-store/ProductDetails/utils'

export const calculateTotalPrice = (
  subTotal: number,
  shippingCost: ShippingOption['price']
): string => {
  if (subTotal && shippingCost) {
    return ((subTotal * 100 + shippingCost) / 100).toFixed(2)
  } else return ''
}

export const getDiscountedAmount = (preDiscountAmount: number, discount: number): number => {
  return roundPrice(preDiscountAmount * (discount / 100))
}

export const convertToPrice = (amount: ShippingOption['price']): string => {
  if (!amount) return '0.00'
  return (amount / 100).toFixed(2)
}

export const calculateStickerPrice = (
  quantity: PriceTier['quantity'],
  unitAmount: PriceTier['unitAmount']
): string => {
  if (!unitAmount || !quantity) return '0.00'
  return ((unitAmount * quantity) / 100).toFixed(2)
}

export const calculateSavedPercent = (
  minUnitAmount: PriceTier['unitAmount'],
  quantity: PriceTier['quantity'],
  unitAmount?: PriceTier['unitAmount']
): number => {
  let savedPercent = 0
  if (!minUnitAmount || !unitAmount || !quantity) return savedPercent
  const hasSavings = minUnitAmount > unitAmount
  if (minUnitAmount && quantity && unitAmount && hasSavings) {
    const actualPrice = Number(calculateStickerPrice(quantity, unitAmount))
    const minUnitAmountPrice = Number(calculateStickerPrice(quantity, minUnitAmount))
    savedPercent = Math.round(((minUnitAmountPrice - actualPrice) / minUnitAmountPrice) * 100)
  }
  return savedPercent
}

export const getMinOrderQuantityPrice = (
  product: PrintStoreProductsType,
  discountPercent?: number
): string => {
  const minOrderQuantityTier = product.priceModel.sortedTiers[0]

  const unitAmount = discountPercent
    ? minOrderQuantityTier.unitAmount * ((100 - discountPercent) / 100)
    : minOrderQuantityTier.unitAmount

  const minOrderQuantityPrice = calculateStickerPrice(minOrderQuantityTier.quantity, unitAmount)
  return `${minOrderQuantityPrice}`
}

export const getPriceForQuantity = (
  product: ShoppingCartItem['product'],
  quantity: PriceTier['quantity']
): GetPriceForQuantityResponse => {
  let price = {
    price: DEFAULT_INITIAL_BILLING_OPTIONS.itemsSubTotal,
    unitAmount: DEFAULT_INITIAL_BILLING_OPTIONS.unitAmount
  }
  if (product?.priceModel?.priceType === PriceType.Fixed) {
    product?.priceModel?.sortedTiers?.forEach(tier => {
      if (tier?.quantity == quantity) {
        price = {
          price: calculateStickerPrice(tier?.quantity, tier?.unitAmount),
          unitAmount: tier?.unitAmount
        }
      }
    })
  }
  return price
}

export const calcHandlingCost = (
  itemsSubtotal: number,
  pricePercentageIncrease: number
): number => {
  return itemsSubtotal * (pricePercentageIncrease / 100)
}

const getNonDeliveryDaysCount = (
  startDay: number,
  maxDays: number,
  nonDeliveryDays?: ShippingOption['excludedDeliveryDaysOfWeek']
): number => {
  const today = new Date()
  let nonWorkingDaysCount = 0
  let maxDaysCount = maxDays
  for (
    let deliveryTransitDays = startDay;
    deliveryTransitDays <= maxDaysCount;
    deliveryTransitDays++
  ) {
    const currentTransitDate = new Date()
    currentTransitDate.setDate(today.getDate() + deliveryTransitDays)
    const currentTransitDay = currentTransitDate.getDay()
    // if there is a non working day the maxDaysCount days increases by 1
    if (nonDeliveryDays && nonDeliveryDays.includes(currentTransitDay)) {
      nonWorkingDaysCount++
      maxDaysCount++
    } else {
      continue
    }
  }
  return nonWorkingDaysCount
}

export const getProductEstimatedDeliveryDate = (
  deliveryDaysRangeMin: ShippingOption['deliveryDaysRangeMin'],
  deliveryDaysRangeMax: ShippingOption['deliveryDaysRangeMax'],
  basicProcessingTime: Product['handlingTimeBusinessDays'],
  nonDeliveryDays?: ShippingOption['excludedDeliveryDaysOfWeek']
): string => {
  if (
    !deliveryDaysRangeMin ||
    !deliveryDaysRangeMax ||
    basicProcessingTime == undefined ||
    basicProcessingTime == null ||
    (nonDeliveryDays && nonDeliveryDays.length >= INVALID_NON_WORKING_DAYS_LENGTH)
  ) {
    return '-'
  } else {
    const today = new Date()
    const estimatedDeliveryStartDate = new Date()
    const estimatedDeliveryEndDate = new Date()

    //calculate the basicProcessingTime + weekends (non working days)
    const nonWorkingBasicProcessingDays = getNonDeliveryDaysCount(
      CURRENT_DAY,
      basicProcessingTime,
      NON_WORKING_WEEKEND_DAYS
    )
    const totalWorkingBasicProcessingTime = basicProcessingTime + nonWorkingBasicProcessingDays

    // min and max delivery days does not include the current day (today)
    const minDeliveryDays = deliveryDaysRangeMin + totalWorkingBasicProcessingTime - CURRENT_DAY
    const maxDeliveryDays = deliveryDaysRangeMax + totalWorkingBasicProcessingTime - CURRENT_DAY

    //calculate the no of non working days then generate the delivery date
    const startDateNonWorkingDays = getNonDeliveryDaysCount(
      CURRENT_DAY,
      minDeliveryDays,
      nonDeliveryDays
    )
    estimatedDeliveryStartDate.setDate(today.getDate() + minDeliveryDays + startDateNonWorkingDays)
    const endDateNonWorkingDays = getNonDeliveryDaysCount(
      CURRENT_DAY,
      maxDeliveryDays,
      nonDeliveryDays
    )
    estimatedDeliveryEndDate.setDate(today.getDate() + maxDeliveryDays + endDateNonWorkingDays)

    const formattedEstimatedDeliveryStartDate = `${
      MONTHS[estimatedDeliveryStartDate.getMonth()]
    } ${estimatedDeliveryStartDate.getDate()}`

    const formattedEstimatedDeliveryEndRange = `${
      MONTHS[estimatedDeliveryEndDate.getMonth()]
    } ${estimatedDeliveryEndDate.getDate()}`

    return `${formattedEstimatedDeliveryStartDate} - ${formattedEstimatedDeliveryEndRange}`
  }
}

export const getIsOutOfStock = (
  availableQuantity: Product['availableQuantity'],
  minOrderQuantity: PriceTier['quantity']
): boolean => {
  //minOrderQuantity - item.priceModel.sortedTiers[0].quantity
  // is the least no of products that can be ordered & cannot be 0.
  // if 0 - the checkout btn will be disabed by checkDisableCheckoutButton fn
  if (!availableQuantity || minOrderQuantity === undefined || minOrderQuantity === null) return true
  return availableQuantity < minOrderQuantity
}
